import React from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Star, CheckCircle, Clock, Phone } from 'lucide-react';

/**
 * DealerCard - Displays dealer information with availability
 * Used in the "Nearby Availability" section of the home page
 */
const DealerCard = ({ dealer, distance = null, vehicleCount = 0 }) => {
  const navigate = useNavigate();

  // Format short address (city, state)
  const formatShortAddress = (address) => {
    if (!address) return 'Address not available';
    const parts = [];
    if (address.city) parts.push(address.city);
    if (address.state) parts.push(address.state);
    return parts.join(', ') || 'Address not available';
  };

  // Format full address
  const formatFullAddress = (address) => {
    if (!address) return null;
    const parts = [];
    if (address.street) parts.push(address.street);
    if (address.city) parts.push(address.city);
    if (address.state && address.zip) {
      parts.push(`${address.state} ${address.zip}`);
    } else if (address.state) {
      parts.push(address.state);
    } else if (address.zip) {
      parts.push(address.zip);
    }
    return parts.length > 0 ? parts.join(', ') : null;
  };

  // Check if dealer is open now
  const isOpenNow = () => {
    if (!dealer.hours || dealer.hours.length === 0) return null;

    const now = new Date();
    const dayOfWeek = now.getDay(); // 0 = Sunday, 6 = Saturday
    const currentTime = now.toTimeString().slice(0, 5); // "HH:MM"

    const todayHours = dealer.hours.find(h => h.day === dayOfWeek);
    if (!todayHours || !todayHours.isOpen) return false;

    return currentTime >= todayHours.open && currentTime <= todayHours.close;
  };

  const openStatus = isOpenNow();

  const handleViewDealer = () => {
    // Navigate to vehicles page filtered by this dealer
    navigate(`/vehicles?dealerId=${dealer._id}`);
  };

  return (
    <div className="bg-app-bg-secondary-light dark:bg-app-bg-secondary rounded-2xl border border-app-border-light dark:border-app-border p-6 hover:border-app-accent-light dark:hover:border-app-accent transition-all duration-200 hover:shadow-xl">
      {/* Header with name and verified badge */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-lg font-semibold text-app-text-light dark:text-app-text">
              {dealer.name}
            </h3>
            {dealer.verified && (
              <CheckCircle className="w-5 h-5 text-app-success" title="Verified Dealer" />
            )}
          </div>

          {/* Location */}
          <div className="flex items-start gap-1 text-sm text-app-text-secondary-light dark:text-app-text-secondary">
            <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <div>
              <span>{formatFullAddress(dealer.address) || formatShortAddress(dealer.address)}</span>
              {distance && (
                <span className="font-medium text-app-accent ml-1">
                  • {distance} miles away
                </span>
              )}
            </div>
          </div>

          {/* Phone */}
          {dealer.phone && (
            <div className="flex items-center gap-1 text-sm text-app-text-secondary-light dark:text-app-text-secondary mt-1">
              <Phone className="w-4 h-4" />
              <a
                href={`tel:${dealer.phone}`}
                className="hover:text-app-accent transition-colors"
                onClick={(e) => e.stopPropagation()}
              >
                {dealer.phone}
              </a>
            </div>
          )}
        </div>

        {/* Rating */}
        {dealer.rating && dealer.rating.count > 0 && (
          <div className="flex items-center gap-1 bg-app-warning/10 px-2 py-1 rounded-lg">
            <Star className="w-4 h-4 fill-app-warning text-app-warning" />
            <span className="text-sm font-semibold text-app-text-light dark:text-app-text">
              {dealer.rating.average.toFixed(1)}
            </span>
            <span className="text-xs text-app-text-secondary-light dark:text-app-text-secondary">
              ({dealer.rating.count})
            </span>
          </div>
        )}
      </div>

      {/* Availability badge */}
      <div className="flex items-center gap-3 mb-4">
        {openStatus !== null && (
          <div className="flex items-center gap-1">
            <Clock className={`w-4 h-4 ${openStatus ? 'text-app-success' : 'text-app-error'}`} />
            <span className={`text-sm font-medium ${openStatus ? 'text-app-success' : 'text-app-error'}`}>
              {openStatus ? 'Open Now' : 'Closed'}
            </span>
          </div>
        )}

        {vehicleCount > 0 && (
          <span className="text-sm text-app-text-secondary-light dark:text-app-text-secondary">
            {vehicleCount} vehicle{vehicleCount !== 1 ? 's' : ''} available
          </span>
        )}
      </div>

      {/* CTA Button */}
      <button
        onClick={handleViewDealer}
        className="w-full bg-app-accent hover:bg-app-accent/90 text-white py-3 rounded-xl font-medium transition-all duration-200 hover:shadow-lg"
      >
        View Available Vehicles
      </button>
    </div>
  );
};

export default DealerCard;
